import React, { useEffect, useState } from "react";
import axios from "axios";
import { BookOpen, ExternalLink, Library, ScrollText, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useAbarytDate } from "../context/AbarytDateContext";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const YAH_SCRIPTURES_URL = "https://yahscriptures.online/";

export default function ScriptureOfDay() {
  const { date, fallbackDate } = useAbarytDate();
  const { user } = useAuth();
  const effectiveDate = date || fallbackDate;
  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(false);

  const month = effectiveDate?.month;
  const day = effectiveDate?.day;
  const dayOfYear = effectiveDate?.dayOfYear;
  const isShabat = Boolean(effectiveDate?.isShabat);
  const gregorianMonth = effectiveDate?.gregorianMonth;
  const gregorianDay = effectiveDate?.gregorianDay;

  useEffect(() => {
    if (!month || !day || !dayOfYear) {
      setPassage(null);
      return;
    }

    let active = true;
    setLoading(true);

    axios
      .get(`${API}/daily-scripture`, {
        params: {
          month,
          day,
          day_of_year: dayOfYear,
          is_shabat: isShabat,
          gregorian_month: gregorianMonth,
          gregorian_day: gregorianDay,
        },
      })
      .then((response) => {
        if (active) setPassage(response.data);
      })
      .catch(() => {
        if (active) setPassage(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [month, day, dayOfYear, isShabat, gregorianMonth, gregorianDay]);

  const sourceUrl = (passage?.source_url || YAH_SCRIPTURES_URL).trim() || YAH_SCRIPTURES_URL;


  return (
    <article
      className="overflow-hidden border border-kingdom-gold/40 bg-white shadow-sm"
      data-testid="scripture-of-day"
    >
      <div className="grid md:grid-cols-[220px_1fr]">
        <div className="bg-royal-blue px-6 py-7 text-white md:py-8">
          <div className="flex items-center gap-2 text-kingdom-gold">
            <BookOpen className="h-5 w-5" aria-hidden="true" />
            <span className="text-xs font-bold uppercase tracking-[0.18em]">
              Scripture of the day
            </span>
          </div>

          {effectiveDate && (
            <div className="mt-5">
              <div className="text-3xl font-heading font-bold">
                {effectiveDate.month}/{effectiveDate.day}
              </div>
              <div className="mt-1 text-xs uppercase tracking-wider text-white/70">
                Abaryt Year {effectiveDate.year}
              </div>
            </div>
          )}

          {passage?.appointed_time && (
            <div className="mt-5 inline-flex bg-kingdom-gold px-3 py-1 text-xs font-bold uppercase tracking-wider text-royal-blue">
              {passage.appointed_time}
            </div>
          )}
        </div>

        <div className="px-6 py-7 sm:px-8 md:py-8">
          {loading ? (
            <p className="text-slate-500">Loading today’s Scripture…</p>
          ) : passage ? (
            <>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-kingdom-gold">
                {passage.theme || "Daily reading"}
              </p>

              <div className="mt-3 font-heading text-2xl leading-relaxed text-slate-800 sm:text-3xl whitespace-pre-line">
                {passage.text ? (
                  passage.text_status === "verified_source" || passage.text_status === "manual_override"
                    ? <>“{passage.text}”</>
                    : passage.text
                ) : <>Read {passage.reference} in YAH Scriptures.</>}
              </div>
              {passage.text_status === "study_excerpt" && (
                <p className="mt-2 text-sm text-slate-600">
                  Study excerpt — not verified word-for-word against YAH Scriptures.
                </p>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <span className="text-lg font-bold text-royal-blue">{passage.reference}</span>
                <span className="text-sm font-semibold text-slate-500">
                  {passage.source || "YAH Scriptures"}
                </span>
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-royal-blue underline underline-offset-4"
                >
                  Open YAH Scriptures <ExternalLink className="h-4 w-4" />
                </a>
              </div>

              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                <Link
                  to={`/daily-devotional/${dayOfYear}`}
                  className="inline-flex min-h-11 items-center justify-center gap-2 bg-royal-blue px-5 py-3 font-bold text-white hover:bg-royal-blue/90"
                >
                  <Sparkles className="h-4 w-4 text-kingdom-gold" />
                  Read today’s devotional
                </Link>
                <Link
                  to="/turah-commands"
                  className="inline-flex min-h-11 items-center justify-center gap-2 border border-royal-blue px-4 py-3 font-semibold text-royal-blue hover:bg-royal-blue/5"
                >
                  <ScrollText className="h-4 w-4" />
                  157 Commands
                </Link>
                <Link
                  to={user ? "/my-library" : "/login?next=/my-library"}
                  className="inline-flex min-h-11 items-center justify-center gap-2 border border-kingdom-gold px-4 py-3 font-semibold text-royal-blue hover:bg-kingdom-gold/10"
                >
                  <Library className="h-4 w-4" />
                  My Library
                </Link>
              </div>
            </>
          ) : (
            <p className="text-slate-500">Today’s Scripture is temporarily unavailable.</p>
          )}
        </div>
      </div>
    </article>
  );
}
